import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../../Common/isRequestCanceled";
import {
  REPORTS_BASE_URL,
  COUPON_REPORT_FACT_API,
} from "../../../../Constants/Config";

const initialState = {
  loading: false,
  CouponReportData: [],
  couponList: [],
  totals: { total_coupons_used: 0, total_discount: 0, total_coupons: 0 },
  byLocation: [],
  status: false,
  successMessage: "",
  error: "",
};

let cancelTokenSource;

// One request replaces the two PHP calls this report used to fire: the old
// screen called ReportingReactapi/coupon_report once with today's date purely
// to read `coupon_list` for the dropdown, then again for the rows. The fact
// endpoint returns both, plus per-outlet breakdowns and window totals.
export const fetchCouponReportFactData = createAsyncThunk(
  "CouponReportFact/fetchCouponReportFactData",
  async (data, { rejectWithValue }) => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Previous request canceled");
    }

    cancelTokenSource = axios.CancelToken.source();

    try {
      const { token, ...dataNew } = data;

      // The fact endpoint is FastAPI and reads Form() fields, so the body is a
      // real multipart FormData and the Content-Type header is left to axios
      // so it can generate the boundary.
      const formData = new FormData();
      const append = (key, value) => {
        if (value === undefined || value === null || value === "") return;
        formData.append(key, value);
      };

      // merchant_id must stay the LOGGED-IN merchant: auth validates the token
      // against it, so sending a selected outlet here is a 401. outlet_ids is
      // what actually scopes the report.
      append("merchant_id", dataNew?.merchant_id);
      // DashDateRangeComponent's Outlet picker is multi-select and emits
      // `stores` as a comma-joined merchant_id list; that IS the multi-location
      // filter. Fall back to the logged-in merchant so a single-store login
      // stays scoped rather than widening to everything.
      append(
        "outlet_ids",
        dataNew?.outlet_ids || dataNew?.stores || dataNew?.merchant_id
      );
      append("start_date", dataNew?.start_date);
      append("end_date", dataNew?.end_date);
      // Left out entirely when every coupon is ticked, which the endpoint
      // reads as "no filter". Sending the full name list instead - what the
      // PHP screen does - silently drops any coupon that has since been
      // deleted, because the list only ever holds CURRENT coupons.
      append("coupon_code", dataNew?.coupon_code);
      append("token_id", dataNew?.token_id);
      append("login_type", dataNew?.login_type);
      append("session_id", dataNew?.session_id);

      const res = await axios.post(
        REPORTS_BASE_URL + COUPON_REPORT_FACT_API,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          cancelToken: cancelTokenSource.token,
        }
      );

      if (res.data.status === true) {
        return {
          arr: res.data.coupon_report_data || [],
          couponList: res.data.coupon_list || [],
          totals: res.data.totals || initialState.totals,
          byLocation: res.data.by_location || [],
          status: (res.data.coupon_report_data || []).length > 0,
        };
      }
      return {
        arr: [],
        couponList: res.data.coupon_list || [],
        totals: initialState.totals,
        byLocation: [],
        status: false,
      };
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const CouponReportFactSlice = createSlice({
  name: "CouponReportFact",
  initialState,
  reducers: {
    updateStateOfCouponFact: (state) => {
      state.CouponReportData = [];
      state.totals = initialState.totals;
      state.byLocation = [];
    },
    updateStatusCouponReportFactData: (state) => {
      state.status = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCouponReportFactData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchCouponReportFactData.fulfilled, (state, action) => {
      state.loading = false;
      state.CouponReportData = action.payload.arr;
      // The dropdown's options come back with the rows, and are built BEFORE
      // the coupon filter is applied, so narrowing the filter never shrinks
      // the list of things you can pick next.
      state.couponList = action.payload.couponList;
      state.totals = action.payload.totals;
      state.byLocation = action.payload.byLocation;
      state.status = action.payload.status;
      state.error = "";
    });
    builder.addCase(fetchCouponReportFactData.rejected, (state, action) => {
      state.loading = false;
      state.CouponReportData = [];
      state.totals = initialState.totals;
      state.byLocation = [];
      state.error = action.error.message;
    });
  },
});

export const { updateStateOfCouponFact, updateStatusCouponReportFactData } =
  CouponReportFactSlice.actions;
export default CouponReportFactSlice.reducer;
